<script setup lang="ts">
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import EmailForm from '@/components/login/EmailForm.vue'
import CodeForm from '@/components/login/CodeForm.vue'
import ResendCodeOrLink from '@/components/login/ResendCodeOrLink.vue'
import { LoginMethod } from '@/types'
import Container from '@/components/shared/Container.vue'
import {
  SET_AUTH_EMAIL,
  SET_FORM_SUBMITTED_STATUS,
} from '@/store/mutation-types'
import Header1 from '@/components/shared/Header1.vue'

const store = useStore()

const codeLogin = ref(false)

const loginHeading = computed(() =>
  codeLogin.value ? 'Login.Code.Header' : 'Login.Link.Header'
)
const loginSubHeading = computed(() =>
  codeLogin.value ? 'Login.Code.SubHeader' : 'Login.Link.SubHeader'
)
const emailSentSubHeader = computed(() =>
  codeLogin.value
    ? 'Login.EmailSent.SubHeaderForCode'
    : 'Login.EmailSent.SubHeaderForLink'
)
const btnLoading = computed(() => store.state.auth.btnLoading)
const formSubmitted = computed(() => store.state.auth.formSubmitted)
const email = computed(() => store.state.auth.authEmail)

const sendEmail = (email: string) => {
  store.dispatch('sendLoginEmail', {
    email,
    send: codeLogin.value ? LoginMethod.CODE : LoginMethod.LINK,
  })
}

const handleCodeLogin = (bool: boolean) => {
  codeLogin.value = bool
}

const clearFormSubmitted = () => {
  store.commit(SET_FORM_SUBMITTED_STATUS, false)
  store.commit(SET_AUTH_EMAIL, '')
}
</script>

<template>
  <Container data-testid="login-view">
    <div v-if="!formSubmitted">
      <div class="row">
        <div class="col mx-auto">
          <img
            id="giftAidHeart"
            :alt="$t('Login.ImageAlt')"
            :aria-label="$t('Login.ImageAriaLabel')"
            src="@/assets/images/giftaidheart.svg"
            data-testid="gift-aid-heart-image"
          />
          <Header1 :heading="loginHeading" />
          <p>{{ $t(loginSubHeading) }}</p>
        </div>
      </div>
      <div class="row">
        <EmailForm
          class="col-md-6 col-lg-4 mx-auto"
          :loading="btnLoading"
          :using-code-login="codeLogin"
          @send="sendEmail"
          @code-login="handleCodeLogin"
        />
      </div>
    </div>

    <div v-else>
      <div class="row">
        <div class="col col-md-6 col-lg-4 mx-auto">
          <img
            id="openEmail"
            :alt="$t('Login.EmailSent.ImageAlt')"
            src="@/assets/images/openEmail.svg"
            height="80px"
          />
          <Header1 heading="Login.EmailSent.Header" />
          <p>
            {{ $t(emailSentSubHeader) }}
            <strong>{{ email }}</strong>
          </p>
          <CodeForm v-if="codeLogin" :email="email" />
          <ResendCodeOrLink
            :is-code-login="codeLogin"
            @resend="sendEmail(email)"
            @use-different-email="clearFormSubmitted"
          />
        </div>
      </div>
    </div>
  </Container>
</template>
